import React, { useEffect, useRef, useState } from 'react';
import { animated, useSpring } from '@react-spring/web';
import { getLegalDocuments } from 'managers/Components.js';
import { useDataStore } from 'files/StateManagement.js';

import Abstract from 'classes/Abstract.js';
import Account from 'classes/Account.js';
import { AddEditAccount, AddEditUser, ConfigureTriggers, IdentityVerification, LegalAgreementsLayer, SetPassword, runTrustedDeviceRegistration, userAccountLegalAgreementIds } from 'managers/Users.js';
import Appearance from 'styles/Appearance.js';
import Button from 'views/Button.js';
import { NewSubscription, SubscriptionPlanSelectionQuiz } from 'managers/Payments.js';
import Request from 'files/Request.js';
import { StartIndex } from 'structure/Layer.js';
import User from 'classes/User.js';

const Onboarding = ({ onClose, utils }) => {

    const scrollContainerRef = useRef(null);
    
    const [activeIndex, setActiveIndex] = useState(null);
    const [animations, animationsAPI] = useSpring(() => ({
        config: { mass: 1, tension: 180, friction: 22 },
        opacity: 0,
        transform: 'scale(0.75)'
    }));
    const [contentAnimations, contentAnimationsAPI] = useSpring(() => ({
        config: { mass: 1, tension: 180, friction: 16 },
        opacity: 0,
        top: -125
    }));
    const [loading, setLoading] = useState(false);
    const { sockets, system, user, setUser } = useDataStore();
    const [visible, setVisible] = useState(false);

    const onAddTrustedDeviceClick = async () => {
        try {

            // set loading flag and submit request to trusted device registration processor
            setLoading('trusted_device');
            let token = await runTrustedDeviceRegistration(utils, user.user_id);

            // save trusted device token to browser for later use
            utils.user.device.set(user.user_id, token);

            // end loading and show confirmation alert
            setTimeout(() => {
                
                // track completion
                utils.analytics.log('onboarding_trusted_device_added');
                
                // show confirmation alert and proceed to next step
                setLoading(false);
                utils.banner.show({
                    title: 'All Done!',
                    message: 'Your trusted device has been successfully registered to your account.',
                    onShow: onRequestNextContent
                });
            }, 500);

        } catch(e){
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue registering your trusted device. ${e.message || 'An unknown error occurred'}`,
                origin: 'onboarding.on_add_trusted_device_click'
            });
        }
    }

    const onConfigureLifeChecksClick = () => {

        // verify that current user has an active subscription
        if(!user?.subscription) {
            utils.alert.show({
                title: 'Subscription Required',
                message: `To enable and configure LifeChecks — our periodic wellness pings that help protect your digital legacy — you’ll need to choose a plan first.`,
                buttons: [{
                    key: 'confirm',
                    title: 'Choose a Plan',
                    style: 'default'
                },{
                    key: 'cancel',
                    title: 'Close',
                    style: 'cancel'
                }],
                onClick: key => {
                    if(key === 'confirm') {
                        utils.layer.open({
                            Component: NewSubscription.bind(this, { freeTrial: true }),
                            id: 'new_subscription'
                        });
                        return;
                    }
                }
            });
            return;
        }

        // open layer for user to configure subscription lifecheck preferences
        utils.layer.open({
            abstract: Abstract.create({
                object: user.subscription,
                type: 'subscriptions'
            }),
            Component: ConfigureTriggers.bind(this, {
                category: 'life_checks',
                onChange: () => {
                    utils.analytics.log('onboarding_lifechecks_configured');
                    onRequestNextContent();
                }
            }),
            id: `life_checks.${user.subscription.id}`
        });
    }

    const onConfirmLegalAgreements = async legalAgreements => {
        return new Promise(async (resolve, reject) => {
            try {

                // submit request to server to send new sms opt in message
                let { agreements } = await Request.post(utils, '/users/', {
                    legal_agreements: legalAgreements,
                    type: 'update_legal_agreements'
                });

                // update user state with completed agreements
                setUser(user => {
                    user.legal_agreements = agreements;
                    user.onboarding.has_completed_legal_agreements = true;
                    return user;
                });

                // track completion event
                utils.analytics.log('onboarding_legal_agreements_accepted');

                // resolve with a confirmation alert
                resolve({ 
                    banner: true,
                    message: `Your responses have been recorded and a confirmation email has been sent to the email address on file with your account.`,
                    title: 'Agreements Accepted',
                    onShow: onRequestNextContent 
                });

            } catch(e) {
                reject(e);
            }
        });
    }

    const onCreatePasswordClick = () => {
        utils.layer.open({
            Component: SetPassword.bind(this, {
                onComplete: onRequestNextContent
            }),
            id: 'set_password'
        });
    }

    const onEditAccountDetailsClick = () => {
        utils.layer.open({
            abstract: Abstract.create({
                object: user,
                type: 'users'
            }),
            Component: AddEditUser.bind(this, {
                isNewTarget: false,
                onComplete: onRequestNextContent,
                shouldShowAccessManagement: false,
                shouldShowCustomizations: false
            }),
            id: `edit_user_${user.user_id}`
        });
    }

    const onExitClick = async () => {

        // log that onboarding was closed 
        let stepKey = sections[activeIndex]?.key;
        if(stepKey === 'completion') {
            utils.analytics.log('sign_up', { method: 'username_and_password' });
            utils.analytics.log('onboarding_completed');
        } else {
            utils.analytics.log('onboarding_exited', { step: stepKey });
        }

        // run animation to hide components
        const animate = () => new Promise(resolve => {
            animationsAPI.start({
                to: async next => {
                    await next({
                        opacity: 0,
                        transform: 'scale(0.75)'
                    });
                    resolve();
                }
            });
        });
        await animate();

        // set visibility flag and notify root that component has closed
        setVisible(false);
        if(typeof(onClose) === 'function') {
            onClose();
        }
    }

    const onLegalAgreementsClick = () => {
        utils.layer.open({
            Component: LegalAgreementsLayer.bind(this, { 
                documentIDs: userAccountLegalAgreementIds,
                legalAgreements: user.legal_agreements,
                onAsyncComplete: onConfirmLegalAgreements
            }),
            id: 'legal_agreements'
        });
    }

    const onNewAccountClick = async () => {
        try {
        
            // verify that user has access to this feature
            await utils.user.capabilities.verify('accounts', 'create');

            // show layer with options to add an account
            utils.layer.open({
                abstract: Abstract.create({
                    object: Account.new(),
                    type: 'accounts'
                }),
                Component: AddEditAccount.bind(this, { 
                    isNewTarget: true,
                    onAccountAdded: () => {
                        utils.analytics.log('onboarding_account_linked');
                        onRequestNextContent();
                    }
                }),
                id: 'new_linked_account'
            });
            
        } catch(e) {
            console.error(e.message);
        }
    }

    const onNewUserAccountClick = () => {
        utils.layer.open({
            abstract: Abstract.create({
                object: User.new(),
                type: 'users'
            }),
            Component: AddEditUser.bind(this, {
                isNewTarget: true,
                onComplete: onRequestNextContent
            }),
            id: 'new_user'
        });
    }

    const onRequestNextContent = async () => {

        // prepare trusted device for current user
        let trustedDevice = user ? utils.user.device.get(user.user_id) : null;

        // prepare index using requested key
        let index = sections.findIndex(section => {

            // determine if no user account is available
            if(!user) {
                return section.key === 'signup'
            }

            // determine if account profile is incomplete
            if(user.onboarding.profile_complete === false) {
                return section.key === 'edit_account_details';
            }

            // determine if asms complaince is missing
            if(user.onboarding.sms_compliance !== true) {
                return section.key === 'sms_compliance';
            }

            // determine if a trusted device needs to be added
            if(trustedDevice === null) {
                return section.key === 'trusted_device';
            }

            // determine if a password needs to be created
            if(user.onboarding.has_configured_password === false) {
                return section.key === 'create_password';
            }

            // determine if legal agreements need to be accepted
            if(user.onboarding.has_completed_legal_agreements === false) {
                return section.key === 'legal_agreements';
            }
            
            // determine if a subscription needs to be purchased
            if(user.onboarding.has_subscription === false) {
                return section.key === 'subscription';
            }

            // determine if a user verification is required
            if(user.verified !== true) {
                return section.key === 'identity_verification';
            }

            // determine if at least one account is present
            if(user.onboarding.has_configured_accounts === false) {
                return section.key === 'accounts';
            }

            // determine if lifechecks have been enabled
            if(user.onboarding.has_configured_life_checks === false) {
                return section.key === 'lifechecks';
            }

            // fallback to completion page
            return section.key === 'completion';
        });

        // animate all components out of view 
        await setContentAnimations('out');

        // set next active index so new content can be prepared
        let nextIndex = index < 0 ? 0 : index;
        setActiveIndex(nextIndex);

        // track onboarding step in analytics
        utils.analytics.log('onboarding_step_viewed', { step: sections[nextIndex]?.key });

        // animate components back into view
        await setContentAnimations('in');
    }

    const onSmsComplianceClick = async () => {
        try {

            // submit request to server to send new sms opt in message
            setLoading('sms_compliance');
            await Request.post(utils, '/users/', {
                type: 'new_sms_opt_in'
            });
            
            setLoading(false);
            utils.banner.show({
                title: 'SMS Opt-In Sent',
                message: `A text message has been sent to the phone number associated with your account. Please follow the instructions in the message to complete the opt-in process.`,
            });

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue sending the SMS opt-in message. ${e.message || 'An unknown error occurred'}`,
                origin: 'onboarding.on_sms_compliance_click'
            });
        }
    }

    const onSmsOptInResult = evt => {
        try {

            // update user state with new compliance result
            setUser(user => {
                user.onboarding.sms_compliance = evt.result;
                return user;
            });

            // show an error message if the opt-in was rejected
            if(evt.result === false) {

                // log that sms verification failed
                utils.analytics.log('onboarding_sms_verification_failed');

                // show error alert for user
                utils.alert.show({
                    title: 'SMS Opt-In Rejected',
                    message: 'SMS opt-in for your phone number was rejected. You will need to complete SMS opt-in to continue using LifeWipe features. Please try the opt-in process again.',
                });
                return;
            }

            // log that verification was successful
            utils.analytics.log('onboarding_sms_verified');

            // proceed to next onboarding step
            setTimeout(onRequestNextContent, 500);

        } catch(e) {
            console.error(e.message);
        }
    }

    const onStartIdentityVerificationClick = () => {
        
        // track user starting identity verification
        utils.analytics.log('onboarding_identity_verification_started');
        
        // present identity verification layer
        user ? presentIdentityVerification() : utils.user.request.login({}, presentIdentityVerification);
    }

    const onStartPlanSelectionClick = () => {
        
        // track user starting plan selection
        utils.analytics.log('onboarding_plan_selection_started');
        
        // present subscription plan selection quiz
        utils.layer.open({
            Component: SubscriptionPlanSelectionQuiz.bind(this, {
                onChange: onRequestNextContent
            }),
            id: 'subscription_plan_selection_quiz'
        });
    }

    const getButtons = () => {
        let content = sections[activeIndex];
        let targets = system.window.isMobile ? content?.buttons.reverse() : content?.buttons;
        return (
            <div 
            className={'row px-2 pb-1 pt-0 p-xl-3 m-0'}
            style={{
                borderTop: `1px solid ${Appearance.colors.divider()}`,
                width: '100%'
            }}>
                {targets?.map((button, index, buttons) => {
                    return (
                        <div
                        key={index}
                        className={`col-12 col-xl-${parseInt(12 / buttons.length)} pt-${index === 0 ? 3 : 2} pb-${index === buttons.length - 1 ? 2 : 0} px-1 py-xl-0 px-xl-1`}>
                            <Button
                            {...button}
                            key={null}
                            label={button.text}
                            type={'large'}/>
                        </div>
                    )
                })}
            </div>
        )
    }

    const getContent = () => {
        
        // no additional logic is required if no index has been set
        if(activeIndex === null) {
            return null;
        }

        // prepare content for index and genreate components
        let content = sections[activeIndex];
        return (
            <animated.div style={{
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                width: '100%',
                ...contentAnimations
            }}>
                <span style={{
                    ...Appearance.textStyles.heavyHeader(),
                    fontFamily: 'Zen Dots',
                    marginBottom: 16,
                    whiteSpace: 'normal'
                }}>{content.title.toUpperCase()}</span>
                <span style={{
                    ...Appearance.textStyles.defaultText(),
                    marginBottom: system.window.isMobile ? 24 : 36
                }}>{content.description}</span>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    width: '100%'
                }}>
                    {content.tips.map((tip, index) => {
                        return (
                            <div 
                            key={index}
                            style={{
                                alignItems: 'center',
                                borderTop: index !== 0 && Appearance.styles.bottomBorder(),
                                display: 'flex',
                                flexDirection: 'row',
                                padding: 16,
                                minWidth: 0,
                                width: '100%'
                            }}>
                                <img
                                className={'d-none d-xl-flex'} 
                                src={window.theme === 'dark' ? 'images/checkmark-badge-dark.png' : 'images/checkmark-badge-light.png'}
                                style={{
                                    height: 45,
                                    marginRight: 18,
                                    objectFit: 'contain',
                                    width: 45
                                }} />
                                <div  style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    flexGrow: 1
                                }}>
                                    <span style={{
                                        ...Appearance.textStyles.defaultText(),
                                        fontWeight: 'bold',
                                        marginBottom: 2
                                    }}>
                                        {tip.title}
                                    </span>
                                    <span style={{
                                        ...Appearance.textStyles.defaultText(),
                                        flexGrow: 1
                                    }}>
                                        {tip.description}
                                    </span>
                                </div>
                            </div>
                        )
                    })}
                </div>
            </animated.div>
        )
    }

    const connectToSockets = () => {

        // listen to relevant account changes
        sockets.on('users', 'sms_opt_in_result', onSmsOptInResult);

        // return function to cleanup subscriptions on unmount
        return () => {
            sockets.off('users', 'sms_opt_in_result', onSmsOptInResult);
        };
    }

    const presentIdentityVerification = () => {

        // no further action is required if user is already verified
        if(user?.verified) {
            utils.alert.show({
                title: 'Identity Verified',
                message: 'Your identity verification has already been successfully completed for this account. No further action is required.',
                onClick: onRequestNextContent
            });
            return;
        }

        // show layer with details about receiving and completing the identity verification
        utils.layer.open({
            Component: IdentityVerification.bind(this, {
                onComplete: onRequestNextContent
            }),
            id: 'identity_verification'
        });
    }

    const runSetup = () => {

        // set first active page based on the user onboarding progress
        onRequestNextContent();

        // animate main component into view
        setVisible(true);
        animationsAPI.start({
            opacity: 1,
            transform: 'scale(1)'
        });
    }

    const setContentAnimations = direction => {
        return new Promise(resolve => {
            contentAnimationsAPI.start({
                to: async next => {
                    await next({
                        opacity: direction === 'in' ? 1 : 0,
                        top: direction === 'in' ? 0 : -125
                    });
                    resolve();
                } 
            });
        });
    }

    const sections = [{
        buttons: [{
            color: 'grey',
            key: 'exit',
            onClick: onExitClick,
            text: 'Exit & Explore'
        },{
            color: 'primary',
            key: 'create_account',
            onClick: onNewUserAccountClick,
            text: 'Create Account'
        }],
        description: `To get started with LifeWipe, you'll first create a secure account. We'll ask for your full name, email address, phone number, and date of birth — this information helps us verify your identity, protect your data, and tailor your emergency response settings.`,
        key: 'signup',
        tips: [{
            description: 'This ensures identity verification and emergency protocols work correctly.',
            title: 'Use your legal name and birthdate'
        },{
            description: `We'll send you LifeCheck alerts and critical security notifications here.`,
            title: 'Choose an email you check regularly'
        },{
            description: 'SMS may be used for identity confirmation, trusted contact alerts, or emergency access.',
            title: 'Use a mobile number you control'
        },{
            description: 'Use a mix of letters, numbers, and symbols. Avoid reusing passwords from other sites.',
            title: 'Create a strong, unique password'
        }],
        title: 'Create Your Account'
    },{
        buttons: [{
            color: 'grey',
            key: 'exit',
            onClick: onExitClick,
            text: 'Exit & Explore'
        },{
            color: 'primary',
            key: 'edit_account_details',
            loading: loading === 'edit_account_details',
            onClick: onEditAccountDetailsClick,
            text: 'Continue'
        }],
        description: `To continue setting up your LifeWipe account, we’ll need a few more details. We’ll ask for your address, phone number, and date of birth — this helps us verify your identity, protect your data, and personalize your emergency response settings.`,
        key: 'edit_account_details',
        tips: [{
            description: 'This ensures identity verification and emergency protocols work correctly.',
            title: 'Use your legal address and date of birth'
        },{
            description: `SMS may be used for identity confirmation, trusted contact alerts, or emergency access.`,
            title: 'Use a mobile number you control'
        },{
            description: 'We respect your privacy and will never share your phone number with third parties without your consent.',
            title: 'Your privacy matters'
        }],
        title: 'Finish Setting Up Your Account'
    },{
        buttons: [{
            color: 'grey',
            key: 'exit',
            onClick: onExitClick,
            text: 'Exit & Explore'
        },{
            color: 'primary',
            key: 'sms_compliance',
            loading: loading === 'sms_compliance',
            onClick: onSmsComplianceClick,
            text: 'Continue'
        }],
        description: `To keep your LifeWipe account secure, we need to verify that you own the phone number you provided during signup. You're about to receive a text message with a verification code — simply enter that code on the next screen to confirm your number and complete this important security step.`,
        key: 'sms_compliance',
        tips: [{
            description: 'We respect your privacy and will never share your phone number with third parties without your consent.',
            title: 'Your privacy matters'
        },{
            description: 'Standard message and data rates may apply based on your mobile plan.',
            title: 'Message & data rates may apply'
        },{
            description: 'If you don’t receive the code within a few minutes, double-check your number and try again.',
            title: 'Didn’t receive the code?'
        }],
        title: 'Verify Your Phone Number'
    },{
        buttons: [{
            color: 'grey',
            key: 'exit',
            onClick: onExitClick,
            text: 'Exit & Explore'
        },{
            color: 'primary',
            key: 'trusted_device',
            loading: loading === 'trusted_device',
            onClick: onAddTrustedDeviceClick,
            text: 'Continue'
        }],
        description: `For your security, LifeWipe only allows access from trusted devices. You're about to register the device you're using now — whether it's a phone, tablet, or computer — as your first trusted device. If this isn’t the device you plan to use regularly, please stop and continue setup from the correct one. Once confirmed, this device will be authorized to access your LifeWipe account.`,
        key: 'trusted_device',
        tips: [{
            description: 'Registering a trusted device helps ensure that only authorized devices can access your account.',
            title: 'Secure your account with trusted devices'
        },{
            description: 'You can manage your trusted devices in your account settings.',
            title: 'Manage your trusted devices'
        },{
            description: 'If this isn’t the device you plan to use regularly, please stop and continue setup from the correct one.',
            title: 'Choose the correct device'
        }],
        title: 'Add a Trusted Device'
    },{
        buttons: [{
            color: 'grey',
            key: 'exit',
            onClick: onExitClick,
            text: 'Exit & Explore'
        },{
            color: 'primary',
            key: 'create_account',
            onClick: onCreatePasswordClick,
            text: 'Create Password'
        }],
        description: `Let's set a strong password to secure your LifeWipe account. A robust password is essential for protecting your personal information and ensuring that only you can access and manage your digital legacy. Consider using a mix of uppercase and lowercase letters, numbers, and special characters to enhance security.`,
        key: 'create_password',
        tips: [{
            description: 'A strong password helps protect your account from unauthorized access.',
            title: 'Use at least 12 characters'
        },{
            description: 'Incorporate uppercase letters, lowercase letters, numbers, and special characters.',
            title: 'Create a complex password'
        },{
            description: `Avoid using easily guessable information like your name, birthdate, or common words.`,
            title: 'Steer clear of personal info and common words'
        }],
        title: 'Create Your Password'
    },{
        buttons: [{
            color: 'grey',
            key: 'exit',
            onClick: onExitClick,
            text: 'Exit & Explore'
        },{
            color: 'primary',
            key: 'continue',
            onClick: onLegalAgreementsClick,
            text: 'Review Agreements'
        }],
        description: `Before you can begin using LifeWipe, you’ll need to review and accept a few important legal agreements. These documents explain how we protect your data, honor your wishes, and deliver our services. Please take a moment to read through each one — your acceptance is required to activate your account and start using LifeWipe.`,
        key: 'legal_agreements',
        tips: userAccountLegalAgreementIds.map(id => {
            let target = getLegalDocuments().find(doc => doc.id === id);
            return {
                description: target.short_description,
                title: target.title
            }
        }),
        title: 'Legal Agreements'
    },{
        buttons: [{
            color: 'grey',
            key: 'exit',
            onClick: onExitClick,
            text: 'Exit & Explore'
        },{
            color: 'primary',
            key: 'select_plan',
            onClick: onStartPlanSelectionClick,
            text: 'Choose a Plan'
        }],
        description: `Your plan determines which features are available to you — like legacy messaging, emergency triggers, or trusted contact access — as well as how many devices and accounts you can protect. You'll answer a few quick questions to help us recommend the subscription plan that best fits your needs. Don't worry — you can always upgrade or downgrade your plan later if your preferences or priorities change.`,
        key: 'subscription',
        tips: [{
            description: 'Whether you want everything wiped, messages delivered, or both — that drives your feature needs.',
            title: `Think about what you'd want to happen if you're unreachable`
        },{
            description: 'If you use multiple devices or platforms (like Apple + Google + Microsoft), choose a plan that supports that scale.',
            title: 'Be realistic about your digital footprint'
        },{
            description: 'If you want others to help manage or trigger your emergency plan, pick a plan that supports trusted contacts.',
            title: 'Consider who you trust'
        },{
            description: 'If you want fast help or personal guidance, opt for a plan with 24/7 support.',
            title: `Support matters if you're privacy-conscious or tech-wary`
        }],
        title: 'Choose a Protection Plan'
    },{
        buttons: [{
            color: 'grey',
            key: 'exit',
            onClick: onExitClick,
            text: 'Exit & Explore'
        },{
            color: 'primary',
            key: 'continue',
            onClick: onStartIdentityVerificationClick,
            text: 'Start Verification'
        }],
        description: `Before you can configure your LifeWipe subscription, you'll need to verify your identity through our trusted third-party partner. This quick, secure process requires uploading a government-issued ID (like a driver's license or passport) to confirm that your name and date of birth match your account details — it's a critical step to protect you and your account.`,
        key: 'identity_verification',
        tips: [{
            description: `Accepted forms include a driver's license, passport, or state-issued ID card.`,
            title: 'Use a valid, government-issued ID'
        },{
            description: `If they don't match, verification may fail or be delayed.`,
            title: 'Make sure your name and date of birth match your LifeWipe account'
        },{
            description: 'Avoid glare, shadows, or blurry images — all text must be readable.',
            title: 'Take a clear photo in good lighting'
        },{
            description: 'Show the full document, including all edges and details.',
            title: `Don't crop or cover parts of the ID`
        }],
        title: 'Verify Your Identity'
    },{
        buttons: [{
            color: 'grey',
            key: 'exit',
            onClick: onExitClick,
            text: 'Exit & Explore'
        },{
            color: 'primary',
            key: 'new_account',
            onClick: onNewAccountClick,
            text: 'Link an Account'
        }],
        description: `Now you'll add your first external account — this is where LifeWipe really goes to work. We recommend starting with an account that matters most to you or holds sensitive information, like your Apple ID, Google account, Facebook, or Instagram. These are often the keys to your digital life, and protecting them ensures your legacy is secure.`,
        key: 'accounts',
        tips: [{
            description: 'Think about your Apple ID, Google account, or anything tied to your identity, photos, finances, or cloud storage.',
            title: 'Start with the accounts that hold the most value.'
        },{
            description: `Facebook, Instagram, and other platforms often become part of your digital legacy — consider how you want them handled.`,
            title: 'Social media matters too.'
        },{
            description: `Don't worry about doing everything right now — just start with one that feels important.`,
            title: 'You can add more accounts anytime.'
        }],
        title: 'Connect an Account'
    },{
        buttons: [{
            color: 'grey',
            key: 'exit',
            onClick: onExitClick,
            text: 'Exit & Explore'
        },{
            color: 'primary',
            key: 'configure_lifecheck',
            onClick: onConfigureLifeChecksClick,
            text: 'Configure LifeChecks'
        }],
        description: `LifeChecks are periodic check-ins to confirm you're safe and in control of your account. If you miss a scheduled check-in, LifeWipe can automatically activate your emergency plan — whether that means wiping devices, sending messages, or alerting trusted contacts. It's a simple but powerful safeguard to ensure your wishes are carried out, even if you're unable to act yourself.`,
        key: 'lifechecks',
        tips: [{
            description: 'If you travel often or live alone, a more frequent check (like every 30 days) might make sense.',
            title: 'Choose a check-in frequency that fits your lifestyle'
        },{
            description: `Give yourself enough time to respond — just in case you're busy, offline, or away.`,
            title: 'Set a realistic grace period'
        },{
            description: `LifeChecks are flexible. You can change how often you're pinged or what actions are tied to them anytime.`,
            title: 'Start simple — you can adjust later'
        },{
            description: 'If something happens and no one knows right away, LifeWipe will — and it can act on your behalf.',
            title: 'Think of LifeChecks as a silent failsafe'
        }],
        title: "Set Up Your LifeChecks",
    },{
        buttons: [{
            color: 'dark',
            key: 'configure_lifecheck',
            onClick: onExitClick,
            text: 'Close'
        }],
        description: `Congratulations — your LifeWipe account is fully set up and ready to protect your digital legacy! You can add more external accounts anytime by visiting the Accounts section, and if you'd like to tweak your LifeCheck settings, just head to the Subscriptions area and click on your active plan. You're in control, and LifeWipe has your back.`,
        key: 'completion',
        tips: [{
            description: `Start with the big ones — Apple, Google, social — then add financial, email, or lesser-used platforms as you go. `,
            title: 'Keep adding accounts over time'
        },{
            description: 'Decide what should happen for each account: wipe it, transfer it, lock it — or leave it untouched.',
            title: 'Set or update your emergency actions'
        },{
            description: 'Make sure the timing and grace period still make sense for your lifestyle.',
            title: 'Review your LifeCheck preferences occasionally.'
        },{
            description: 'Add video, notes, or files for loved ones. You can update them as your story evolves.',
            title: 'Revisit your messages and legacy content.'  
        },{
            description: 'Make sure they know what LifeWipe is and how they may be involved if needed.',
            title: 'Check in with trusted contacts.'
        },{
            description: `We'll continue to add new tools — keep an eye out.`,
            title: 'Explore the dashboard for insights and updates.' 
        }],
        title: "🚀 Setup Complete — You're Protected"
    }];

    useEffect(() => {
        if(scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = 0;
        }
    }, [activeIndex]);

    useEffect(() => {
        runSetup();
        let disconnect = connectToSockets();
        return () => {
            disconnect();
        }
    }, []);

    return visible === true && (
        <div style={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            justifyContent: 'center',
            position: 'fixed',
            width: '100vw',
            zIndex: StartIndex - 1
        }}>
            <animated.div style={{
                alignItems: 'center',
                backgroundColor: Appearance.colors.dim,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                height: '100%',
                position: 'fixed',
                opacity: animations.opacity,
                width: '100vw'
            }} />
            <animated.div 
            className={system.window.isMobile === true ? 'safe-area-bottom-padding' : ''}
            style={{
                ...Appearance.styles.unstyledPanel(),
                display: 'flex',
                flexDirection: 'column',
                height: 'auto',
                maxHeight: 'calc(100% - 128px)',
                maxWidth: 750,
                ...animations,
                ...system.window.isMobile && {
                    border: 'none',
                    borderRadius: 0,
                    height: '100vh',
                    maxHeight: '100vh',
                    maxWidth: '100%'
                }
            }}>
                <div 
                ref={scrollContainerRef}
                style={{
                    flexGrow: 1,
                    overflowY: 'scroll',
                    padding: system.window.isMobile ? 16 : 24,
                    position: 'relative',
                    width: '100%'
                }}>
                    {getContent()}
                </div>
                {getButtons()}
            </animated.div>
        </div>
    )
}

export default Onboarding;